import React from 'react';
import { Box, useSnackbar } from 'pounceV2';
import {
  useGetAsyncQueryDownloadUrlLazyQuery,
  useStartAsyncDownloadOfAllQueryResults,
} from 'Pages/Investigate/DataExplorer/Results/ResultsTab/DownloadAllButton/graphql/getAsyncQueryDownloadUrl.generated';
import { parseQueryFilter } from 'Pages/Investigate/QueryBuilder/helpers/helpers';
import { GetAsyncQueryDownloadUrlInput } from 'Generated/schema';
import { SimpleSearchQueryFilter } from 'Pages/Investigate/QueryBuilder/types';
import { TimeFrameEnum } from 'Pages/Investigate/QueryBuilder/CrossLogSearch/types';
import { useGetDatalakeQueryLazyQuery } from 'Pages/Investigate/QueryBuilder/graphql/getDatalakeQuery.generated';
import { getSelectedColumnPaths } from 'Pages/Investigate/QueryBuilder/CrossLogSearch/helpers/utils';
import { useGetAsyncQueryResultsLazyQuery } from 'Hooks/graphql/getAsyncQueryResults.generated';
import { parseJSONWithBigInt } from 'Helpers/utils';
import { useNotifications } from 'Components/Notifications/graphql/Notifications.generated';
import { useUserConfigContext } from 'Pages/Investigate/QueryBuilder/SearchTabs/UserConfigContext';
import { useAppConfig } from 'Components/utils/AppBootstrapper/AppConfigContext';

interface UseCSVDownloadParams {
  // The main query ID used to re-run query and generate the CSV in an async download
  queryId: string;
  // The dependent query ID used when not async downloading
  downloadQueryId?: string;
  isAsyncDownload?: boolean;
}

export const useCSVDownloadV3 = ({
  queryId,
  downloadQueryId,
  isAsyncDownload,
}: UseCSVDownloadParams) => {
  const { pushSnackbar } = useSnackbar();
  // hiddenStaticColumns mirrors the results-table per-column hide state so the CSV exports exactly
  // the static columns the table is showing.
  const { userConfig, hiddenStaticColumns } = useUserConfigContext();
  const { pantherDataLakeType } = useAppConfig();
  const [getDatalakeQuery, { loading: dataLakeQueryLoading }] = useGetDatalakeQueryLazyQuery();
  const [getAsyncQueryResults, { loading: asyncQueryResultsLoading }] =
    useGetAsyncQueryResultsLazyQuery();

  const { refetch } = useNotifications({
    variables: { input: { pageSize: 10 } },
    pollInterval: 0, // Disable automatic polling
    skip: !queryId,
  });

  const [startAsyncDownloadOfAllQueryResults, { loading }] = useStartAsyncDownloadOfAllQueryResults(
    {
      onCompleted: () => {
        pushSnackbar({
          variant: 'info',
          title: (
            <Box maxWidth={250}>
              Generating CSV... this may take some time. You will be notified in app when the file
              is ready
            </Box>
          ),
          position: 'bottom-left',
          duration: 8000,
          discardable: true,
        });

        // notifications are polled every minute in the NotificationMenu but some
        // downloads are quick, so this fetches the CSV notifications that are already done
        setTimeout(() => refetch(), 5000);
      },
      onError: () => {
        pushSnackbar({
          variant: 'error',
          title: 'There was an error generating your CSV',
        });
      },
    },
  );

  const [getDownloadUrl] = useGetAsyncQueryDownloadUrlLazyQuery({
    // FIXME: If we don't add  `network-only, the `onCompleted` is only fired on the first click
    //  and not on any subsequent ones
    // https://github.com/apollographql/apollo-client/issues/6636
    // https://github.com/apollographql/react-apollo/issues/4000
    fetchPolicy: 'network-only',
    onCompleted: urlData => {
      if (urlData.getAsyncQueryDownloadUrl.error) {
        pushSnackbar({
          variant: 'error',
          title: `Failed to download`,
          description: urlData.getAsyncQueryDownloadUrl.error.message,
          duration: 5000,
        });
      } else {
        window.location.href = urlData.getAsyncQueryDownloadUrl.url;
      }
    },
    onError: () => {
      pushSnackbar({
        variant: 'error',
        title: `Failed to download`,
      });
    },
  });

  const getDownloadData = React.useCallback(async () => {
    if (!queryId) {
      return null;
    }

    const [{ data: datalakeQueryData }, { data: queryResultsData }] = await Promise.all([
      getDatalakeQuery({
        variables: { id: queryId },
      }),
      getAsyncQueryResults({
        variables: { id: queryId },
      }),
    ]);

    const queryData = datalakeQueryData?.dataLakeQuery?.metadata;
    const dependentQueryData = queryResultsData?.dataLakeQuery?.results?.edges?.map(edge => {
      return parseJSONWithBigInt(edge.nodeAsString);
    });
    const language = datalakeQueryData?.dataLakeQuery?.kind.includes('simple search')
      ? 'simpleSearch'
      : 'pantherflow';
    const columns = getSelectedColumnPaths(
      userConfig,
      dependentQueryData,
      language,
      pantherDataLakeType,
      hiddenStaticColumns,
    );

    return { columns, language, queryData };
  }, [
    getAsyncQueryResults,
    getDatalakeQuery,
    pantherDataLakeType,
    queryId,
    userConfig,
    hiddenStaticColumns,
  ]);

  const downloadQueryResults = async () => {
    const downloadData = await getDownloadData();
    if (!downloadData) {
      return;
    }

    const { columns } = downloadData;
    const variables = { input: { id: downloadQueryId } };
    if (columns.length) {
      (variables.input as GetAsyncQueryDownloadUrlInput).columns = columns;
    }
    await getDownloadUrl({ variables });
  };

  const startAsyncDownload = async () => {
    const downloadData = await getDownloadData();
    if (!downloadData) {
      return;
    }

    const { columns, language, queryData } = downloadData;
    const filters = queryData?.simpleSearchQueryFilter;
    const relativeTime = queryData?.relativeTime;

    await startAsyncDownloadOfAllQueryResults({
      variables: {
        input: {
          params: {
            databases: queryData?.databases,
            tables: queryData?.tables,
            startTime: relativeTime === TimeFrameEnum.AllTime ? undefined : queryData?.startTime,
            endTime: relativeTime === TimeFrameEnum.AllTime ? undefined : queryData?.endTime,
            // we don't send the relative time so the download will always be for the data the user was seeing
            relativeTime:
              relativeTime === TimeFrameEnum.AllTime ? TimeFrameEnum.AllTime : undefined,
            sortDir: queryData?.sortDirection,
            language,
            filters:
              language === 'simpleSearch'
                ? JSON.stringify(
                    filters
                      ? parseQueryFilter(filters as unknown as SimpleSearchQueryFilter)
                      : { ors: [] },
                  )
                : queryData?.query,
            parameters: queryData?.parameters?.map(
              ({ Name, Statement }: { Name: string; Statement: string }) => ({
                name: Name,
                statement: Statement,
              }),
            ),
          },
          originalQueryId: queryId,
          columns,
        },
      },
    });
  };

  return {
    downloadCSVResults:
      // from a Notification, there is no downloadQueryId, but also a notification
      // is also only made for queries that are downloaded async, so this should never be a problem
      isAsyncDownload || !downloadQueryId ? startAsyncDownload : downloadQueryResults,
    loading: loading || dataLakeQueryLoading || asyncQueryResultsLoading,
  };
};
